import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { PALETTE, formatBucket } from 'entities/analytics/lib';
import { useUsersCount } from 'entities/analytics/model';
import { AnalyticsCard } from 'entities/analytics/ui/analytics-card';
import { EChart } from 'entities/analytics/ui/echart';

import { AnalyticsRangeParams } from 'shared/api';

type Props = { params: AnalyticsRangeParams };

export const UsersCountCard = ({ params }: Props) => {
  const granularity = params.granularity ?? 'month';
  const { data, isLoading, isFetching } = useUsersCount(params);
  const series = useMemo(() => data?.data.series ?? [], [data]);
  const totalRegistrations = data?.data.totalRegistrations ?? 0;
  const totalDeletions = data?.data.totalDeletions ?? 0;

  const option = useMemo<EChartsOption>(
    () => ({
      grid: { left: 8, right: 16, top: 12, bottom: 24, containLabel: true },
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0, textStyle: { color: PALETTE.axis, fontSize: 11 }, itemHeight: 9 },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: series.map((p) => formatBucket(p.bucket, granularity)),
        axisLine: { lineStyle: { color: '#dfe5e3' } },
        axisLabel: { color: PALETTE.axis, fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: PALETTE.grid } },
        axisLabel: { color: PALETTE.axis, fontSize: 10 },
      },
      series: [
        {
          name: 'Registrations',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: series.map((p) => p.registrations),
          lineStyle: { color: PALETTE.primary },
          itemStyle: { color: PALETTE.primary },
        },
        {
          name: 'Deletions',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: series.map((p) => p.deletions),
          lineStyle: { color: PALETTE.danger },
          itemStyle: { color: PALETTE.danger },
        },
      ],
    }),
    [series, granularity],
  );

  return (
    <AnalyticsCard
      title="Users count"
      subtitle="Registrations and deletions"
      totals={[
        { label: 'New', value: totalRegistrations.toLocaleString() },
        { label: 'Deleted', value: totalDeletions.toLocaleString() },
      ]}
      isLoading={isLoading}
      isFetching={isFetching}
      isEmpty={!isLoading && series.length === 0}
      footer="Deletions tracked since the analytics module release. Earlier periods show 0."
      chartHeight={210}
      chart={<EChart option={option} height={210} />}
    />
  );
};
